import { useEffect, useState } from 'react'

import { Button, Input, Label } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ArticleInputAutocomplete } from '@/components/common'
import { DateInput } from '@/components/ui/DateInput'
import { useWarehouseStore } from '@/store/warehouse.store'
import { todayISO } from '@/utils/dates'
import { numericOnly } from '@/utils/input'

const articleSchema = z.object({
  name: z.string().min(1, 'Укажите наименование детали').max(512, 'Слишком длинное наименование'),
  description: z.string().max(512, 'Слишком длинное описание').optional(),
  date: z.string().min(1, 'Укажите дату поступления'),
  count: z.number({ error: 'Укажите количество' }).int('Количество должно быть целым числом').min(1, 'Количество должно быть больше 0'),
  cost: z.number({ error: 'Укажите цену за единицу' }).min(0, 'Цена не может быть отрицательной'),
})

type FormValues = z.infer<typeof articleSchema>;

interface WarehouseArticleFormProps {
  articleId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function WarehouseArticleForm({ articleId, onSuccess, onCancel }: WarehouseArticleFormProps) {
  const { t } = useTranslation()
  const isEdit = Boolean(articleId)

  const { history, addArticle, updateArticle, isLoading } = useWarehouseStore()
  const existing = articleId ? history.find(a => a.id === articleId) : undefined

  const [costInput, setCostInput] = useState(existing ? String(existing.cost).replace('.', ',') : '')

  useEffect(() => {
    if (existing) {
      setCostInput(String(existing.cost).replace('.', ','))
    } else {
      setCostInput('')
    }
  }, [existing])

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(articleSchema),
    defaultValues: existing
      ? {
          name: existing.parsedName,
          description: existing.description || '',
          date: existing.date,
          count: existing.count,
          cost: existing.cost,
        }
      : { name: '', description: '', date: todayISO(), count: 1, cost: 0 },
  })

  useEffect(() => {
    if (existing) {
      reset({
        name: existing.parsedName,
        description: existing.description || '',
        date: existing.date,
        count: existing.count,
        cost: existing.cost,
      })
    } else {
      reset({ name: '', description: '', date: todayISO(), count: 1, cost: 0 })
    }
  }, [existing, reset])

  const onSubmit = async (data: FormValues) => {
    const payload = {
      name: data.name.trim(),
      description: data.description?.trim() || '',
      date: data.date,
      count: data.count,
      cost: data.cost,
    }

    try {
      if (isEdit && articleId) {
        await updateArticle(articleId, payload)
      } else {
        await addArticle(payload)
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
      {/* Part Name */}
      <div className="flex flex-col gap-1">
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }) => (
            <ArticleInputAutocomplete
              label={t('warehouse.form.name')}
              placeholder="Введите название детали"
              value={field.value}
              onChange={field.onChange}
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              required
            />
          )}
        />
      </div>

      {/* Description */}
      <div className="flex flex-col gap-1">
        <Label htmlFor="article-description">{t('warehouse.form.description', 'Описание детали')}</Label>
        <Input
          id="article-description"
          type="text"
          placeholder={t('warehouse.form.description_placeholder', 'Введите описание детали (опционально)')}
          className={errors.description ? 'border-danger' : ''}
          {...register('description')}
          variant="secondary"
        />
        {errors.description?.message && <span className="text-xs text-danger">{errors.description.message}</span>}
      </div>

      {/* Date */}
      <div className="flex flex-col gap-1">
        <Controller
          name="date"
          control={control}
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label={t('warehouse.form.date')}
              required
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </div>

      {/* Count */}
      <div className="flex flex-col gap-1">
        <Label htmlFor="article-count" isRequired>{t('warehouse.form.count')}</Label>
        <Input
          id="article-count"
          type="number"
          min="1"
          step="1"
          className={errors.count ? 'border-danger' : ''}
          {...register('count', { valueAsNumber: true })}
          onKeyDown={e => {
            if (
              [
                'Backspace',
                'Tab',
                'Enter',
                'Escape',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Home',
                'End',
              ].includes(e.key) ||
              ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
            ) {
              return
            }
            if (!/^[0-9]$/.test(e.key)) {
              e.preventDefault()
            }
          }}
          variant={'secondary'}
        />
        {errors.count?.message && <span className="text-xs text-danger">{errors.count.message}</span>}
      </div>

      {/* Cost */}
      <div className="flex flex-col gap-1">
        <Label htmlFor="article-cost" isRequired>{t('warehouse.form.cost')}</Label>
        <Controller
          name="cost"
          control={control}
          render={({ field }) => (
            <Input
              id="article-cost"
              ref={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              value={costInput}
              type="text"
              onKeyDown={numericOnly}
              placeholder="0.00"
              variant="secondary"
              className={errors.cost ? 'border-danger' : ''}
              onChange={e => {
                const valStr = e.target.value.replace('.', ',')
                setCostInput(valStr)
                const parsedVal = Number(valStr.replace(',', '.'))
                const finalVal = isNaN(parsedVal) ? 0 : parsedVal
                field.onChange(finalVal)
              }}
            />
          )}
        />
        {errors.cost?.message && <span className="text-xs text-danger">{errors.cost.message}</span>}
      </div>

      <div className="flex gap-3 pt-4 justify-end">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" isDisabled={isLoading}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEdit ? t('common.save') : t('warehouse.add_article')}
            </div>
          ) : (
            isEdit ? t('common.save') : t('warehouse.add_article')
          )}
        </Button>
      </div>
    </form>
  )
}
